import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * Escape a string for safe insertion into HTML text content.
 * @param {string} value
 * @returns {string}
 */
const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] ?? char)
  );

/**
 * Escape a string for safe insertion into an HTML attribute value.
 * @param {string} value
 * @returns {string}
 */
const escapeAttr = (value) => escapeHtml(value);

const RECENT_SEARCHES_KEY = 'cb-recent-searches';
const RECENT_SEARCHES_MAX = 6;

/** @returns {string[]} the shopper's recent search terms, most-recent first. */
function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((t) => typeof t === 'string').slice(0, RECENT_SEARCHES_MAX) : [];
  } catch {
    return [];
  }
}

/** @param {string} term - store a submitted search term (deduped, capped). */
function saveRecentSearch(term) {
  const value = (term || '').trim();
  if (value.length < 2) return;
  try {
    const next = getRecentSearches().filter((existing) => existing.toLowerCase() !== value.toLowerCase());
    next.unshift(value);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(next.slice(0, RECENT_SEARCHES_MAX)));
  } catch {
    /* localStorage unavailable / quota — recent searches are best-effort */
  }
}

function clearRecentSearches() {
  try {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLFormElement} [form] - The predictive search form element.
 * @property {HTMLElement} [skuResults] - The SKU-match results container (rendered above native results).
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // Recent searches: capture submitted terms, handle the "Clear" button, and
    // render the recent pills into the empty state.
    this.addEventListener('click', this.#handleSuggestionClick, { signal });
    this.refs.form?.addEventListener('submit', () => saveRecentSearch(this.refs.searchInput.value), { signal });
    this.#renderRecentSearches();

    onDocumentLoaded(this.#getRecentlyViewed);
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    // Tab and Shift+Tab are deliberately not handled: the search sits in the header's tab order, and
    // capturing them trapped keyboard users in the field (WCAG 2.1.2). Arrow keys move through results.
    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          saveRecentSearch(this.refs.searchInput.value);
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    // An empty input always resets — including the native search-field clear
    // "✕", which fires an input event with no inputType. This must run BEFORE
    // the inputType guard below, otherwise clearing leaves stale results.
    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    // Ignore non-text events (e.g. pressing Escape) that aren't real input.
    if (!event.inputType) return;

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
    this.#getSkuResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Shopify's predictive search defaults to products only. Also request
    // collections and suggested searches (queries) — but NOT pages/articles, so
    // blog posts don't clutter the shopping results. Limit each type to keep the
    // dropdown concise.
    url.searchParams.set('resources[type]', 'product,collection,query');
    url.searchParams.set('resources[limit]', '5');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
        this.#highlightMatches(searchTerm);
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Look up products by SKU via the Cloudflare Worker (Admin API proxy) and
   * render a "Matching part numbers" group above the native predictive results.
   * Shopify's native search can't match variant SKUs, so this fills that gap
   * without putting SKUs in product titles. No-op if no endpoint is configured.
   * @param {string} searchTerm - The term to search for
   */
  #getSkuResults(searchTerm) {
    const endpoint = this.dataset.skuSearchUrl;
    if (!endpoint || !(this.refs.skuResults instanceof HTMLElement)) return;
    if (searchTerm.length < 2) {
      this.#clearSkuResults();
      return;
    }

    const url = new URL(endpoint);
    url.searchParams.set('q', searchTerm);

    fetch(url.toString())
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return;
        // Ignore stale responses if the user has kept typing.
        if (this.refs.searchInput.value.trim() !== searchTerm) return;
        this.#renderSkuResults(data.results || []);
      })
      .catch(() => {
        // Network/worker error — silently skip the SKU group; native results
        // still render so search never breaks because of this enhancement.
      });
  }

  /**
   * Render the SKU match group.
   * @param {Array<{sku:string,title:string,variant_title:string,url:string,image:string,price:string}>} results
   */
  #renderSkuResults(results) {
    const container = this.refs.skuResults;
    if (!(container instanceof HTMLElement)) return;
    if (!Array.isArray(results) || results.length === 0) {
      this.#clearSkuResults();
      return;
    }

    const rows = results
      .map((p) => {
        const image = p.image
          ? `<img class="predictive-search-sku__img" src="${escapeAttr(p.image)}" alt="" width="44" height="44" loading="lazy">`
          : '<span class="predictive-search-sku__img predictive-search-sku__img--empty"></span>';
        const price = p.price ? `<span class="predictive-search-sku__price">$${escapeHtml(p.price)}</span>` : '';
        const variant = p.variant_title
          ? ` &middot; <span class="predictive-search-sku__variant">${escapeHtml(p.variant_title)}</span>`
          : '';
        return (
          `<a class="predictive-search-sku__row" href="${escapeAttr(p.url)}">` +
          image +
          '<span class="predictive-search-sku__info">' +
          `<span class="predictive-search-sku__title">${escapeHtml(p.title)}${variant}</span>` +
          `<span class="predictive-search-sku__sku">SKU: ${escapeHtml(p.sku)}</span>` +
          '</span>' +
          price +
          '</a>'
        );
      })
      .join('');

    container.innerHTML = `<p class="predictive-search-sku__heading">Matching part numbers</p>${rows}`;
    container.hidden = false;
    // Suppress the native "no results" message when SKU matches exist, so the
    // dropdown doesn't say "nothing found" while showing part-number matches.
    this.classList.add('has-sku-matches');
    this.#highlightMatches(this.refs.searchInput.value.trim());
  }

  #clearSkuResults() {
    this.classList.remove('has-sku-matches');
    const container = this.refs.skuResults;
    if (container instanceof HTMLElement) {
      container.innerHTML = '';
      container.hidden = true;
    }
  }

  /**
   * Bold the matched query text inside result titles (products + SKU group).
   * Operates on text nodes only, so nested markup is preserved.
   * @param {string} term - The search term to highlight.
   */
  #highlightMatches(term) {
    const value = (term || '').trim();
    if (value.length < 2) return;

    const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');
    const titles = this.querySelectorAll('.resource-card__title, .predictive-search-sku__title');

    titles.forEach((el) => {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      /** @type {Text[]} */
      const textNodes = [];
      while (walker.nextNode()) {
        const node = /** @type {Text} */ (walker.currentNode);
        // Skip text already inside a highlight mark.
        if (!node.parentElement?.closest('mark.predictive-search-highlight')) textNodes.push(node);
      }

      for (const node of textNodes) {
        const text = node.nodeValue || '';
        regex.lastIndex = 0;
        if (!regex.test(text)) continue;

        regex.lastIndex = 0;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
          }
          const mark = document.createElement('mark');
          mark.className = 'predictive-search-highlight';
          mark.textContent = match[0];
          fragment.appendChild(mark);
          lastIndex = match.index + match[0].length;
          if (match[0].length === 0) regex.lastIndex++;
        }
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }
        node.parentNode?.replaceChild(fragment, node);
      }
    });
  }

  /**
   * Populate the "Recent searches" pills in the empty state from localStorage.
   * The "Popular searches" pills are rendered server-side in the empty state.
   */
  #renderRecentSearches() {
    const group = this.querySelector('[data-recent-group]');
    const pills = this.querySelector('[data-recent-pills]');
    if (!(group instanceof HTMLElement) || !(pills instanceof HTMLElement)) return;

    const recents = getRecentSearches();
    if (recents.length === 0) {
      group.hidden = true;
      pills.innerHTML = '';
      return;
    }

    const base = new URL(Theme.routes.search_url, location.origin);
    pills.innerHTML = recents
      .map((term) => {
        base.searchParams.set('q', term);
        return `<a class="predictive-search-suggested__pill" href="${escapeAttr(base.pathname + base.search)}">${escapeHtml(term)}</a>`;
      })
      .join('');
    group.hidden = false;
  }

  /**
   * Delegated click handler for the "Clear" button on recent searches.
   * @param {MouseEvent} event
   */
  #handleSuggestionClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('[data-clear-recent]')) {
      event.preventDefault();
      clearRecentSearches();
      this.#renderRecentSearches();
    }
  };

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Fetch recently viewed products using the section renderer and update the results container.
   */
  #getRecentlyViewed = async () => {
    const { predictiveSearchResults } = this.refs;
    // Get the initial height before the results are rendered
    const abortController = this.#createAbortController();

    const resultsMarkup = await this.#getRecentlyViewedProductsMarkup();
    if (!resultsMarkup) return;

    const parsedNextPage = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const recentlyViewedProductsHtml = parsedNextPage.getElementById('predictive-search-products');
    if (!recentlyViewedProductsHtml) return;

    for (const child of recentlyViewedProductsHtml.children) {
      if (child instanceof HTMLElement) {
        child.setAttribute('ref', 'recentlyViewedWrapper');
      }
    }

    const collectionElement = predictiveSearchResults.querySelector('#predictive-search-products');
    if (!collectionElement) return;

    if (this.refs.recentlyViewedWrapper) {
      this.refs.recentlyViewedWrapper.remove();
    }

    if (abortController.signal.aborted) return;
    // Prepend the recently viewed products to the collection
    collectionElement.prepend(...recentlyViewedProductsHtml.children);
  };

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#renderRecentSearches();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
